import React, { useEffect, useState, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from './data/supabaseClient';
import { AuthContext } from './Provider';
import { useChat } from './Chat/ChatContext';
import { useToast } from './hooks/ToastContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Components
import RecentPosts from './RecentPosts';
import MyActivePosts from './MyActivePosts';
import Spinner from './common/Spinner';
import PostDetailsModal from './PostDetailsModal';

import CustomButton9, {
  Checkbox1,
  CustomButton1,
  CustomButton10,
  CustomButton11,
  CustomButton2,
  CustomButton3,
  CustomButton4,
  CustomButton5,
  CustomButton6,
  CustomButton7,
  CustomButton8,
  CustomInputBox2,
  ModernCard,
  RefreshButton,
  SearchButton3
} from './Templates';
import {
  FaCalendarAlt,
  FaClock,
  FaTimes,
  FaFilter,
  FaTh,
  FaThLarge,
  FaThList,
  FaChevronDown,
  FaMapMarkerAlt,
  FaExchangeAlt
} from 'react-icons/fa';

// Feature flags
import { FEATURES } from '../config/features';

// Styles
import './Home.css';

/**
 * Home component is the main dashboard page after login
 * Shows search filters, recent posts and navigation options
 */
function Home() {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const { openChat } = useChat();
  const { user, profileLoading } = useContext(AuthContext);
  const { addToast } = useToast();

  
  // Helper function to filter own posts from RecentPosts
  const filterOwnPosts = (posts) => {
    if (!user) return posts;
    return posts.filter(post => post.user_id !== user.id);
  };
  
  // State for feed refresh functionality
  const [feedRefreshState, setFeedRefreshState] = useState({
    newPostsCount: 0,
    isRefreshing: false,
    lastRefreshTime: new Date()
  });
  const recentPostsRef = useRef();
  
  // State for loading, errors, filters and checkboxes
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    // Load saved preference or default to 'list' (dense, full-width rows).
    return localStorage.getItem('recentPostsViewMode') || 'list';
  });
  const [filters, setFilters] = useState({
    roles: ['Driver', 'Passenger', 'Package'], // Initialize with all roles
    from: '',
    to: '',
    dateFrom: null,
    dateTo: null,
    timeFrom: null,
    timeTo: null
  });
  const [filterInputs, setFilterInputs] = useState({
    driversChecked: true, // Default to checked
    passengersChecked: true, // Default to checked
    packagesChecked: true, // Default to checked
    fromValue: '',
    toValue: '',
    dateFrom: null,
    dateTo: null,
    timeFrom: null,
    timeTo: null
  });
  
  // UI state for date/time pickers
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  // Which quick-preset is active (so the pill can show "This week" / "Morning"
  // instead of a raw range). 'custom' means a hand-picked date range.
  const [datePreset, setDatePreset] = useState(null); // 'today' | 'tomorrow' | 'week' | 'custom' | null
  const [timePreset, setTimePreset] = useState(null); // 'morning' | 'afternoon' | 'evening' | null
  // Refs for the From/To inputs (Google Places autocomplete attaches to these).
  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);
  
  // First-visit default is 'list' everywhere (dense, full-width rows). Keep the
  // hook for first-visit tweaks, but no longer force compact on mobile.
  useEffect(() => {
    const handleInitialResize = () => {
      // Only auto-switch if user hasn't made a choice yet (first visit)
      const hasUserPreference = localStorage.getItem('recentPostsViewMode');
      if (!hasUserPreference && window.innerWidth < 768) {
        setViewMode('list');
      }
    };
    
    // Only check on initial load, not on every resize
    handleInitialResize();
  }, []); // Empty dependency array - runs only once on mount

  // Save view mode preference
  useEffect(() => {
    localStorage.setItem('recentPostsViewMode', viewMode);
  }, [viewMode]);
  // Initialize to false - let RecentPosts handle initial load
  const [applyFilters, setApplyFilters] = useState(false);
  
  // Modal state for post and event details
  const [postModalState, setPostModalState] = useState({
    isOpen: false,
    id: null,
    type: null
  });
  
  const [eventModalState, setEventModalState] = useState({
    isOpen: false,
    id: null
  });

  // Listen for openPostDetailsModal events
  useEffect(() => {
    const handleOpenPostDetailsModal = (event) => {
      const { id, type } = event.detail;
      if (id && type) {
        console.log('Home component received openPostDetailsModal event:', id, type);
        setPostModalState({
          isOpen: true,
          id,
          type
        });
      }
    };
    
    window.addEventListener('openPostDetailsModal', handleOpenPostDetailsModal);
    
    return () => {
      window.removeEventListener('openPostDetailsModal', handleOpenPostDetailsModal);
    };
  }, []);

  useEffect(() => {
    const checkSession = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          throw new Error(error.message);
        }
        
        if (!data.session) {
          navigate('/login');
          return;
        }
        
        console.log('Valid session found');
        
        // Check if user profile is complete
        if (data.session.user) {
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('completion_step, plan_type, soft_launch_basic_claimed, basic_claim_date')
            .eq('id', data.session.user.id)
            .single();
            
          // Plan welcome message — only while payments are enabled (everyone is on
          // the unlimited tier during the payments pause, so no plan toast).
          if (FEATURES.PAYMENTS_ENABLED && profile?.soft_launch_basic_claimed && profile?.basic_claim_date) {
            const claimDate = new Date(profile.basic_claim_date);
            const today = new Date();
            const isToday = claimDate.toDateString() === today.toDateString();
            
            // Show welcome message if claimed today and they haven't seen it yet
            if (isToday && !sessionStorage.getItem(`basic_welcome_shown_${data.session.user.id}`)) {
              setTimeout(() => {
                addToast({
                  type: 'success',
                  title: '🎉 Welcome to Basic Plan!',
                  description: 'You now have access to package delivery, ad-free experience, and more posts per month!'
                });
                sessionStorage.setItem(`basic_welcome_shown_${data.session.user.id}`, 'true');
              }, 1500);
            }
          }
            
          if (profileError) {
            console.warn('Could not fetch profile completion status:', profileError);
            // If profile doesn't exist, user needs to complete it
            if (profileError.code === 'PGRST116') {
              console.log('No profile found - redirecting to profile completion');
              navigate('/complete-profile', { replace: true });
              return;
            }
          } else if (profile) {
            // Check if profile setup is incomplete
            if (profile.completion_step < 3) {
              console.log('Profile incomplete (step:', profile.completion_step, ') - redirecting to profile completion');
              navigate('/complete-profile', { replace: true });
              return;
            }
          }
        }
      } catch (err) {
        console.error('Session check error:', err);
        setError(err.message);
        
        addToast({
          type: 'error',
          title: t('errors.session_error'),
          description: err.message
        });
        
        setTimeout(() => navigate('/login'), 3000);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkSession();
  }, [navigate, addToast, t]);
  
  // Listen for country changes and refresh feed
  useEffect(() => {
    const handleCountryChange = (event) => {
      const { newCountry, message } = event.detail;
      console.log('🌍 Country changed to:', newCountry);
      
      // Show success message
      addToast({
        type: 'success',
        title: '🌍 Country Updated',
        description: message || `Country changed to ${newCountry}. Refreshing feed to show relevant content.`
      });
      
      // Refresh the feed to show posts from the new country
      setTimeout(() => {
        if (recentPostsRef.current) {
          recentPostsRef.current.refresh();
        }
      }, 500);
    };
    
    window.addEventListener('countryChanged', handleCountryChange);
    
    return () => {
      window.removeEventListener('countryChanged', handleCountryChange);
    };
  }, [addToast]);

  /**
   * Handle keyboard events for search
   */
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  /**
   * Handle feed refresh functionality
   */
  const handleFeedRefresh = async () => {
    if (recentPostsRef.current && !feedRefreshState.isRefreshing) {
      await recentPostsRef.current.refresh();
    }
  };

  /**
   * Handle refresh state changes from RecentPosts
   */
  const handleRefreshStateChange = (newState) => {
    setFeedRefreshState(newState);
  };

  /**
   * Handle search filter application
   */
  const handleSearch = () => {
    try {
      console.log('Search button clicked, processing filters...');
      
      const selectedRoles = [];
      if (filterInputs.driversChecked) selectedRoles.push('Driver');
      if (filterInputs.passengersChecked) selectedRoles.push('Passenger');
      if (filterInputs.packagesChecked) selectedRoles.push('Package');
      
      console.log('Selected roles:', selectedRoles, 'Package checked:', filterInputs.packagesChecked);
      
      // Only update filters when search button is clicked
      setFilters({
        roles: selectedRoles,
        from: filterInputs.fromValue.trim(),
        to: filterInputs.toValue.trim(),
        dateFrom: filterInputs.dateFrom,
        dateTo: filterInputs.dateTo,
        timeFrom: filterInputs.timeFrom,
        timeTo: filterInputs.timeTo
      });
      
      // Set flag to apply filters
      console.log('Setting applyFilters to true to trigger data refresh');
      
      // Make sure Package role is handled correctly
      if (filterInputs.packagesChecked) {
        if (!selectedRoles.includes('Package')) {
          console.log('Package checkbox is checked but missing from roles - adding it');
          selectedRoles.push('Package');
        }
        
        // Additional log when only Package is selected to help debug
        if (filterInputs.packagesChecked && !filterInputs.driversChecked && !filterInputs.passengersChecked) {
          console.log('*** ONLY PACKAGE CHECKBOX SELECTED ***');
        }
      }
      
      // Final update to filters with possibly corrected roles array
      setFilters({
        roles: selectedRoles,
        from: filterInputs.fromValue.trim(),
        to: filterInputs.toValue.trim(),
        dateFrom: filterInputs.dateFrom,
        dateTo: filterInputs.dateTo,
        timeFrom: filterInputs.timeFrom,
        timeTo: filterInputs.timeTo
      });
      
      setApplyFilters(true);
      
      // Log the state of the package checkbox and roles array
      console.log('Package checkbox state:', filterInputs.packagesChecked);
      console.log('Final roles array for filtering:', selectedRoles);
      
      // Provide feedback that filters were applied
      addToast({
        type: 'info',
        title: t('filters.applied'),
        description: selectedRoles.length === 0 && !filterInputs.fromValue.trim() && !filterInputs.toValue.trim() 
          ? 'Showing all posts - no filters selected' 
          : t('filters.filters_applied')
      });
    } catch (err) {
      console.error('Search error:', err);
      addToast({
        type: 'error',
        title: t('errors.search_error'),
        description: err.message
      });
    }
  };

  /**
   * Handle creating a new post
   */
  const handleAddPostClick = () => {
    navigate("/PublishPost");
  };

  /**
   * Handle sending a package
   */
  const handleSendPackageClick = () => {
    navigate("/SendPackage");
  };

  /**
   * Navigate to Events page
   */
  const handleEventsButtonClick = () => {
    navigate("/events");
  };

  /**
   * Navigate to Groups page
   */
  const handleGroupsButtonClick = () => {
    navigate('/groups');
  };
  
  /**
   * Handle date/time filter functions
   */
  const toggleDatePicker = () => {
    setShowDatePicker(!showDatePicker);
    setShowTimePicker(false); // Close time picker when opening date picker
  };

  const toggleTimePicker = () => {
    setShowTimePicker(!showTimePicker);
    setShowDatePicker(false); // Close date picker when opening time picker
  };

  // Locale for the inline range calendar (locales registered by UnifiedDateTimePicker).
  const datepickerLocale = i18n.language === 'lv' ? 'lv' : i18n.language === 'ru' ? 'ru' : 'en';

  // Build a Date at local noon so date-only filtering (toISOString → YYYY-MM-DD)
  // can't roll to the previous/next day across timezones.
  const atNoon = (d) => { const x = new Date(d); x.setHours(12, 0, 0, 0); return x; };
  const timeAt = (h, m = 0) => { const d = new Date(); d.setHours(h, m, 0, 0); return d; };

  // Date quick presets → write the same dateFrom/dateTo the search already consumes.
  const applyDatePreset = (preset) => {
    const today = new Date();
    if (preset === 'today') {
      const d = atNoon(today);
      setFilterInputs(prev => ({ ...prev, dateFrom: d, dateTo: d }));
    } else if (preset === 'tomorrow') {
      const tm = new Date(today); tm.setDate(tm.getDate() + 1);
      const d = atNoon(tm);
      setFilterInputs(prev => ({ ...prev, dateFrom: d, dateTo: d }));
    } else if (preset === 'week') {
      // Today through the upcoming Sunday (week starts Monday).
      const end = new Date(today);
      const daysToSunday = (7 - end.getDay()) % 7; // 0 when today is Sunday
      end.setDate(end.getDate() + daysToSunday);
      setFilterInputs(prev => ({ ...prev, dateFrom: atNoon(today), dateTo: atNoon(end) }));
    }
    setDatePreset(preset);
    setShowDatePicker(false);
  };

  // Custom range from the inline selectsRange calendar.
  const handleRangeChange = (dates) => {
    const [start, end] = dates;
    setFilterInputs(prev => ({
      ...prev,
      dateFrom: start ? atNoon(start) : null,
      dateTo: end ? atNoon(end) : null
    }));
    setDatePreset('custom');
    if (start && end) setShowDatePicker(false); // both ends picked → done
  };

  // Time-of-day buckets → coarse timeFrom/timeTo ranges.
  const applyTimePreset = (preset) => {
    if (preset === 'any') {
      setFilterInputs(prev => ({ ...prev, timeFrom: null, timeTo: null }));
      setTimePreset(null);
    } else {
      const ranges = {
        morning: [timeAt(6, 0), timeAt(11, 59)],
        afternoon: [timeAt(12, 0), timeAt(17, 59)],
        evening: [timeAt(18, 0), timeAt(23, 59)]
      };
      const [from, to] = ranges[preset];
      setFilterInputs(prev => ({ ...prev, timeFrom: from, timeTo: to }));
      setTimePreset(preset);
    }
    setShowTimePicker(false);
  };

  const clearDateFilters = () => {
    setFilterInputs(prev => ({ ...prev, dateFrom: null, dateTo: null }));
    setDatePreset(null);
  };

  const clearTimeFilters = () => {
    setFilterInputs(prev => ({ ...prev, timeFrom: null, timeTo: null }));
    setTimePreset(null);
  };

  const hasDateFilters = filterInputs.dateFrom || filterInputs.dateTo;
  const hasTimeFilters = filterInputs.timeFrom || filterInputs.timeTo;

  // Short date for the filled pill range, e.g. "06/08".
  const fmtShortDate = (d) => (d ? d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' }) : '');

  // What the Date pill shows when filled: preset label, or the picked range.
  const dateSummary = () => {
    if (datePreset && datePreset !== 'custom') {
      return t(`filters.${datePreset === 'week' ? 'this_week' : datePreset}`);
    }
    const { dateFrom, dateTo } = filterInputs;
    if (dateFrom && dateTo) {
      return dateFrom.getTime() === dateTo.getTime()
        ? fmtShortDate(dateFrom)
        : `${fmtShortDate(dateFrom)} – ${fmtShortDate(dateTo)}`;
    }
    if (dateFrom) return fmtShortDate(dateFrom);
    if (dateTo) return fmtShortDate(dateTo);
    return '';
  };

  // What the Time pill shows when filled: bucket label, or the raw time range.
  const timeSummary = () => {
    if (timePreset) return t(`filters.${timePreset}`);
    const { timeFrom, timeTo } = filterInputs;
    if (timeFrom && timeTo) return `${formatDisplayTime(timeFrom)} – ${formatDisplayTime(timeTo)}`;
    if (timeFrom) return formatDisplayTime(timeFrom);
    if (timeTo) return formatDisplayTime(timeTo);
    return '';
  };

  /**
   * Format date for display
   */
  const formatDisplayDate = (date) => {
    if (!date) return null;
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  /**
   * Format time for display
   */
  const formatDisplayTime = (time) => {
    if (!time) return null;
    return time.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit', 
      hour12: false 
    });
  };

  // Toggle a role filter, but never allow all three off — downstream, "all off"
  // is treated identically to "all on" (shows everything) with no cue, so we
  // block deselecting the last remaining role.
  const toggleRole = (field) => {
    setFilterInputs(prev => {
      const next = { ...prev, [field]: !prev[field] };
      if (!next.driversChecked && !next.passengersChecked && !next.packagesChecked) {
        return prev;
      }
      return next;
    });
  };

  const swapLocations = () => {
    setFilterInputs(prev => ({ ...prev, fromValue: prev.toValue, toValue: prev.fromValue }));
  };

  // Wire Google Places autocomplete onto the From/To inputs (same pattern as
  // AddLocationModal). Waits for the Maps script, cleans up its listeners.
  useEffect(() => {
    let fromAC = null;
    let toAC = null;
    let cancelled = false;

    const attach = (inputEl, field) => {
      const ac = new window.google.maps.places.Autocomplete(inputEl, {
        fields: ['name', 'formatted_address']
      });
      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        const value = place?.name || place?.formatted_address;
        if (value) setFilterInputs(prev => ({ ...prev, [field]: value }));
      });
      return ac;
    };

    const setup = () => {
      if (cancelled) return;
      if (!window.google?.maps?.places || !fromInputRef.current || !toInputRef.current) {
        setTimeout(setup, 400);
        return;
      }
      fromAC = attach(fromInputRef.current, 'fromValue');
      toAC = attach(toInputRef.current, 'toValue');
    };

    setup();

    return () => {
      cancelled = true;
      if (window.google?.maps?.event) {
        if (fromAC) window.google.maps.event.clearInstanceListeners(fromAC);
        if (toAC) window.google.maps.event.clearInstanceListeners(toAC);
      }
    };
  }, []);

  // Handle closing pickers when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.date-time-filter')) {
        setShowDatePicker(false);
        setShowTimePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle post/package view in modal
  const handleViewPost = (id, type) => {
    setPostModalState({
      isOpen: true,
      id,
      type
    });
  };
  
  // Handle event view in modal
  const handleViewEvent = (id) => {
    setEventModalState({
      isOpen: true,
      id
    });
  };
  
  // Close post modal
  const closePostModal = () => {
    setPostModalState({
      isOpen: false,
      id: null,
      type: null
    });
  };
  
  // Close event modal
  const closeEventModal = () => {
    setEventModalState({
      isOpen: false,
      id: null
    });
  };

  // Show loading spinner while checking authentication or profile - but faster
  if (isLoading || profileLoading) {
    return (
      <main className="home-main">
        <div className="home-loading-container">
          <Spinner 
            size="medium" 
            customText={profileLoading ? t('app.loading_profile') : t('app.loading')}
            showText={true}
          />
        </div>
      </main>
    );
  }

  // Show error message if there was a problem
  if (error) {
    return (
      <div className="home-error-container">
        <h2>{t('errors.error_title')}</h2>
        <p>{error}</p>
        <CustomButton2 
          label={t('buttons.retry')} 
          onClick={() => window.location.reload()}
        />
      </div>
    );
  }

  return (
    <>
      <main className="home-main">
        {/* My Active Posts Component - load after initial render */}
        {user && (
          <React.Suspense fallback={<div style={{ height: '120px' }}></div>}>
            <MyActivePosts />
          </React.Suspense>
        )}
        
        <div className="top-containers">
          <div className="top-box-main">
          <div className="top-box-transparent">
            {/* Top row with Add Post button */}
            <div className="driver-passenger-container">
              <CustomButton6
                label={t('buttons.add_post')}
                onClick={handleAddPostClick}
                className="wsCreateBtn wsCreateBtn--ride"
              />
              <CustomButton6
                label={t('buttons.package_post')}
                onClick={handleSendPackageClick}
                className="wsCreateBtn wsCreateBtn--package"
              />
            </div>

            {/* Search section */}
            <div className="search-container">
              {/* From / To location inputs with a swap control between them */}
              <div className="wsloc-fields">
                <div className="wsloc-input">
                  <FaMapMarkerAlt className="wsloc-pin wsloc-pin--from" />
                  <input
                    ref={fromInputRef}
                    type="text"
                    className="wsloc-text"
                    placeholder={t('forms.traveling_from')}
                    value={filterInputs.fromValue}
                    onChange={(e) => setFilterInputs({ ...filterInputs, fromValue: e.target.value })}
                    onKeyDown={handleKeyDown}
                  />
                  {filterInputs.fromValue && (
                    <button
                      type="button"
                      className="wsloc-clear"
                      aria-label={t('buttons.clear') || 'Clear'}
                      onClick={() => setFilterInputs({ ...filterInputs, fromValue: '' })}
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  className="wsloc-swap"
                  onClick={swapLocations}
                  aria-label={t('buttons.swap') || 'Swap from and to'}
                  title={t('buttons.swap') || 'Swap'}
                >
                  <FaExchangeAlt />
                </button>

                <div className="wsloc-input">
                  <FaMapMarkerAlt className="wsloc-pin wsloc-pin--to" />
                  <input
                    ref={toInputRef}
                    type="text"
                    className="wsloc-text"
                    placeholder={t('forms.traveling_to')}
                    value={filterInputs.toValue}
                    onChange={(e) => setFilterInputs({ ...filterInputs, toValue: e.target.value })}
                    onKeyDown={handleKeyDown}
                  />
                  {filterInputs.toValue && (
                    <button
                      type="button"
                      className="wsloc-clear"
                      aria-label={t('buttons.clear') || 'Clear'}
                      onClick={() => setFilterInputs({ ...filterInputs, toValue: '' })}
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>
              </div>

              {/* Role filters — chip toggles (real checkboxes underneath for a11y) */}
              <div className="date-time-filters">
               <div className="checkbox-search-container wsrole-row" onKeyDown={handleKeyDown}>
                {[
                  { field: 'driversChecked', label: t('posts.driver') },
                  { field: 'passengersChecked', label: t('posts.passenger') },
                  { field: 'packagesChecked', label: t('posts.package') }
                ].map(({ field, label }) => (
                  <label key={field} className={`wsrole-chip ${filterInputs[field] ? 'selected' : ''}`}>
                    <input
                      type="checkbox"
                      className="wsrole-checkbox"
                      checked={filterInputs[field]}
                      onChange={() => toggleRole(field)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
               </div>
            </div>
              <div className="date-time-container">
                {/* Date filter — quick chips + a single inline range calendar for "Custom" */}
                <div className="date-time-filter">
                  <button
                    type="button"
                    className={`wsfilter-pill ${hasDateFilters ? 'filled' : ''} ${showDatePicker ? 'open' : ''}`}
                    onClick={toggleDatePicker}
                    aria-expanded={showDatePicker}
                  >
                    <FaCalendarAlt className="wsfilter-pill-icon" />
                    <span className="wsfilter-pill-text">
                      {hasDateFilters ? dateSummary() : t('filters.date')}
                    </span>
                    {hasDateFilters ? (
                      <span
                        className="wsfilter-clear"
                        role="button"
                        tabIndex={0}
                        aria-label={t('buttons.clear') || 'Clear'}
                        onClick={(e) => { e.stopPropagation(); clearDateFilters(); }}
                      >
                        <FaTimes />
                      </span>
                    ) : (
                      <FaChevronDown className={`wsfilter-caret ${showDatePicker ? 'open' : ''}`} />
                    )}
                  </button>

                  {showDatePicker && (
                    <ModernCard className="wsfilter-popover" onClick={(e) => e.stopPropagation()}>
                      <div className="wsfilter-chip-row">
                        <button type="button" className={`wsfilter-chip ${datePreset === 'today' ? 'selected' : ''}`} onClick={() => applyDatePreset('today')}>{t('filters.today')}</button>
                        <button type="button" className={`wsfilter-chip ${datePreset === 'tomorrow' ? 'selected' : ''}`} onClick={() => applyDatePreset('tomorrow')}>{t('filters.tomorrow')}</button>
                        <button type="button" className={`wsfilter-chip ${datePreset === 'week' ? 'selected' : ''}`} onClick={() => applyDatePreset('week')}>{t('filters.this_week')}</button>
                        <button type="button" className={`wsfilter-chip ${datePreset === 'custom' ? 'selected' : ''}`} onClick={() => setDatePreset('custom')}>{t('filters.custom')}</button>
                      </div>
                      {datePreset === 'custom' && (
                        <div className="wsfilter-calendar">
                          <DatePicker
                            selected={filterInputs.dateFrom}
                            onChange={handleRangeChange}
                            startDate={filterInputs.dateFrom}
                            endDate={filterInputs.dateTo}
                            selectsRange
                            inline
                            monthsShown={1}
                            minDate={new Date()}
                            calendarStartDay={1}
                            locale={datepickerLocale}
                          />
                        </div>
                      )}
                    </ModernCard>
                  )}
                </div>

                {/* Time filter — coarse time-of-day buckets */}
                <div className="date-time-filter">
                  <button
                    type="button"
                    className={`wsfilter-pill ${hasTimeFilters ? 'filled' : ''} ${showTimePicker ? 'open' : ''}`}
                    onClick={toggleTimePicker}
                    aria-expanded={showTimePicker}
                  >
                    <FaClock className="wsfilter-pill-icon" />
                    <span className="wsfilter-pill-text">
                      {hasTimeFilters ? timeSummary() : t('filters.time')}
                    </span>
                    {hasTimeFilters ? (
                      <span
                        className="wsfilter-clear"
                        role="button"
                        tabIndex={0}
                        aria-label={t('buttons.clear') || 'Clear'}
                        onClick={(e) => { e.stopPropagation(); clearTimeFilters(); }}
                      >
                        <FaTimes />
                      </span>
                    ) : (
                      <FaChevronDown className={`wsfilter-caret ${showTimePicker ? 'open' : ''}`} />
                    )}
                  </button>

                  {showTimePicker && (
                    <ModernCard className="wsfilter-popover wsfilter-popover--end" onClick={(e) => e.stopPropagation()}>
                      <div className="wsfilter-chip-row">
                        <button type="button" className={`wsfilter-chip ${!timePreset ? 'selected' : ''}`} onClick={() => applyTimePreset('any')}>{t('filters.any')}</button>
                        <button type="button" className={`wsfilter-chip ${timePreset === 'morning' ? 'selected' : ''}`} onClick={() => applyTimePreset('morning')}>{t('filters.morning')}</button>
                        <button type="button" className={`wsfilter-chip ${timePreset === 'afternoon' ? 'selected' : ''}`} onClick={() => applyTimePreset('afternoon')}>{t('filters.afternoon')}</button>
                        <button type="button" className={`wsfilter-chip ${timePreset === 'evening' ? 'selected' : ''}`} onClick={() => applyTimePreset('evening')}>{t('filters.evening')}</button>
                      </div>
                    </ModernCard>
                  )}
                </div>

                <SearchButton3
                  size="small"
                  onClick={handleSearch}
                  className="home-search-button"
                />
              </div>
            </div>

            {/* Events and Groups buttons - conditionally rendered based on feature flags */}
            {(FEATURES.GROUPS_ENABLED || FEATURES.EVENTS_ENABLED) && (
              <div className="events-advanced-search-container">
                {FEATURES.GROUPS_ENABLED && (
                  <CustomButton6
                    label={t('navigation.groups')}
                    onClick={handleGroupsButtonClick} 
                  />
                )}
                {FEATURES.EVENTS_ENABLED && (
                  <CustomButton6
                    label={t('navigation.events')}
                    onClick={handleEventsButtonClick} 
                  />
                )}
              </div>
            )}
          </div>
          </div>
        </div>
        

      
        {/* Recent posts section - optimized loading */}
        <div className="recent-posts-container">
          <div className="recent-posts-header">
            <div className="header-controls">
              {/* View Mode Toggle */}
              <div className="view-mode-toggle">
                <button 
                  className={`mode-btn ${viewMode === 'regular' ? 'active' : ''}`}
                  onClick={() => setViewMode('regular')}
                  title={t('posts.regular_view_tooltip')}
                  type="button"
                >
                  <FaThLarge />
                </button>
                <button
                  className={`mode-btn ${viewMode === 'compact' ? 'active' : ''}`}
                  onClick={() => setViewMode('compact')}
                  title={t('posts.compact_view_tooltip')}
                  type="button"
                >
                  <FaTh />
                </button>
                <button
                  className={`mode-btn ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setViewMode('list')}
                  title={t('posts.list_view_tooltip')}
                  type="button"
                >
                  <FaThList />
                </button>
              </div>
              
              {/* Existing Refresh Button */}
              <div className="refresh-container">
                <RefreshButton
                  onClick={handleFeedRefresh}
                  disabled={feedRefreshState.isRefreshing}
                  tooltip={feedRefreshState.newPostsCount > 0 ? 
                    t('posts.refresh_with_new_count', { count: feedRefreshState.newPostsCount }) :
                    t('posts.refresh_feed')
                  }
                  size="medium"
                  className={feedRefreshState.newPostsCount > 0 ? 'has-new-posts' : ''}
                />
                {feedRefreshState.newPostsCount > 0 && (
                  <span className="new-posts-badge">
                    {feedRefreshState.newPostsCount}
                  </span>
                )}
              </div>
            </div>
          </div>
          {user && (
            <RecentPosts 
              key={`recent-posts`}
              filters={filters} 
              useModals={true} 
              onViewPost={handleViewPost}
              currentUser={user}
              filterOwnPosts={filterOwnPosts}
              applyFilters={applyFilters}
              setApplyFilters={setApplyFilters}
              refreshRef={recentPostsRef}
              onRefreshStateChange={handleRefreshStateChange}
              viewMode={viewMode}
            />
          )}
        </div>
      </main>
      
      {/* Modals */}
      <PostDetailsModal
        isOpen={postModalState.isOpen}
        onClose={closePostModal}
        id={postModalState.id}
        type={postModalState.type}
      />
    </>
  );
}

export default Home;